import { Box } from "@chakra-ui/react";
import React, { useEffect, useRef } from "react";

import { Button, Icon, Typography } from "components";

import styles from "./FilterBase.module.css";

interface FilterBaseProps {
  title: string;
  children: React.ReactNode;
  onClear?: () => void;
  onApply?: () => void;
  onDismiss?: () => void;
  disableClear?: boolean;
  disableApply?: boolean;
  isQuestionnaire?: boolean;
  subtitle?: string;
}

export const FilterBase = ({
  title,
  subtitle,
  children,
  onClear,
  onApply,
  onDismiss,
  disableClear = false,
  disableApply = false,
  isQuestionnaire = false,
}: FilterBaseProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: PointerEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        // Always call onDismiss; let new filter open in the same event naturally.
        requestAnimationFrame(() => {
          onDismiss?.();
        });
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setTimeout(() => {
          onDismiss?.();
        }, 0);
      }
    }

    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onDismiss]);

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${
        isQuestionnaire ? styles.wrapperQuestionnaire : ""
      }`}
    >
      <div className={styles.header}>
        <div className={styles.tail}>
          <Icon name="tooltip-tail" />
        </div>
        <div className={styles.title}>
          <Typography
            variant={isQuestionnaire ? "body" : "small"}
            weight="medium"
          >
            {title}
          </Typography>
          {subtitle && (
            <Box mt={2}>
              <Typography variant="body" weight="medium" color="muted">
                {subtitle}
              </Typography>
            </Box>
          )}
        </div>
        <div className={styles.content}>{children}</div>
      </div>
      <div className={styles.actions}>
        {onClear && (
          <button
            onClick={onClear}
            className={styles.clear}
            disabled={disableClear}
          >
            Clear
          </button>
        )}
        {isQuestionnaire ? (
          <Button
            onClick={onApply}
            disabled={isQuestionnaire && disableApply}
            variant="primary"
            color="black"
          >
            Continue
          </Button>
        ) : (
          <button onClick={onApply} className={styles.apply}>
            Apply
          </button>
        )}
      </div>
    </div>
  );
};
